/**
 * satellites.js – TLE fetch / cache
 *
 * HOW TO ADD A NEW SATELLITE OR CONSTELLATION
 * ─────────────────────────────────────────────
 * Add an entry to SAT_CONFIG below — nothing else needs to change.
 *
 * "single"  →  one tracked object with a dedicated sidebar card (ISS, Hubble…)
 *   { type, label, color (Three.js 0x…), cssColor, badge, url, urlFallback? }
 *
 * "constellation"  →  many sats shown as dots in sidebar + map + globe
 *   { type, label, shortLabel, color, cssColor, url, maxDisplay?, showTrack? }
 */

export const SAT_CONFIG = {

  // ── Single objects ─────────────────────────────────────
  iss: {
    type:        "single",
    label:       "ISS",
    color:       0x00e5ff,
    cssColor:    "#00e5ff",
    badge:       "LEO",
    url:         "https://celestrak.org/NORAD/elements/supplemental/sup-gp.php?CATNR=25544&FORMAT=TLE",
    urlFallback: "https://celestrak.org/NORAD/elements/gp.php?CATNR=25544&FORMAT=TLE",
  },
  hst: {
    type:      "single",
    label:     "Hubble ST",
    color:     0xc084fc,
    cssColor:  "#c084fc",
    badge:     "LEO",
    url:       "https://celestrak.org/NORAD/elements/gp.php?CATNR=20580&FORMAT=TLE",
  },

  // ── Constellations ─────────────────────────────────────
  gps: {
    type:       "constellation",
    label:      "GPS",
    shortLabel: "GPS",
    color:      0x4ade80,
    cssColor:   "#4ade80",
    url:        "https://celestrak.org/NORAD/elements/gp.php?GROUP=gps-ops&FORMAT=TLE",
    maxDisplay: 32,
    showTrack:  false,
  },
  glonass: {
    type:       "constellation",
    label:      "GLONASS",
    shortLabel: "GLO",
    color:      0xfbbf24,
    cssColor:   "#fbbf24",
    url:        "https://celestrak.org/NORAD/elements/gp.php?GROUP=glonass-ops&FORMAT=TLE",
    maxDisplay: 24,
    showTrack:  false,
  },
  galileo: {
    type:       "constellation",
    label:      "Galileo",
    shortLabel: "GAL",
    color:      0xf87171,
    cssColor:   "#f87171",
    url:        "https://celestrak.org/NORAD/elements/gp.php?GROUP=galileo&FORMAT=TLE",
    maxDisplay: 30,
    showTrack:  false,
  },
  beidou: {
    type:       "constellation",
    label:      "BeiDou",
    shortLabel: "BDS",
    color:      0x818cf8,
    cssColor:   "#818cf8",
    url:        "https://celestrak.org/NORAD/elements/gp.php?GROUP=beidou&FORMAT=TLE",
    maxDisplay: 35,
    showTrack:  false,
  },

  // ── Add more here — everything else follows automatically ──
  // starlink: {
  //   type: "constellation", label: "Starlink", shortLabel: "SLK",
  //   color: 0x60a5fa, cssColor: "#60a5fa",
  //   url: "https://celestrak.org/NORAD/elements/gp.php?GROUP=starlink&FORMAT=TLE",
  //   maxDisplay: 40, showTrack: false,
  // },
};

// ── Internal cache ────────────────────────────────────────
const CACHE_TTL_MS = 30 * 60 * 1000;
let cache = {};

export function parseTLE(raw) {
  const lines = raw.split("\n").map((l) => l.trim()).filter(Boolean);
  const sats  = [];
  let i = 0;
  while (i < lines.length) {
    if (
      i + 2 < lines.length &&
      lines[i + 1].startsWith("1 ") &&
      lines[i + 2].startsWith("2 ")
    ) {
      sats.push({ name: lines[i], tle1: lines[i + 1], tle2: lines[i + 2] });
      i += 3;
    } else {
      i++;
    }
  }
  return sats;
}

async function fetchTLE(key) {
  const now = Date.now();
  if (cache[key] && now - cache[key].ts < CACHE_TTL_MS) return cache[key].data;

  const cfg = SAT_CONFIG[key];
  if (!cfg) throw new Error(`Unknown sat key: ${key}`);

  let res = await fetch(cfg.url, { cache: "no-store" });
  if (!res.ok && cfg.urlFallback)
    res = await fetch(cfg.urlFallback, { cache: "no-store" });
  if (!res.ok) throw new Error(`TLE fetch failed for ${key}: ${res.status}`);

  const raw    = await res.text();
  const parsed = parseTLE(raw);
  if (!parsed.length) throw new Error(`No TLE parsed for ${key}`);

  const capped = cfg.maxDisplay ? parsed.slice(0, cfg.maxDisplay) : parsed;
  cache[key]   = { ts: now, data: capped };
  return capped;
}

/**
 * Fetch TLEs for every key in SAT_CONFIG.
 * Uses Promise.allSettled so one failure doesn't block the rest.
 * Returns { [key]: TLEEntry[] }
 */
export async function loadAllTLEs() {
  const keys    = Object.keys(SAT_CONFIG);
  const results = await Promise.allSettled(keys.map((k) => fetchTLE(k)));
  const out     = {};
  keys.forEach((k, i) => {
    if (results[i].status === "fulfilled") out[k] = results[i].value;
    else { console.warn(`TLE fetch failed for ${k}:`, results[i].reason); out[k] = []; }
  });
  return out;
}

export function clearTLECache() {
  cache = {};
}